import { formatDistanceToNow } from "date-fns";
import { FileText, RefreshCw, Trash2 } from "lucide-react";
import type { RagDocument } from "../../services/rag";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";
import { StatusBadge } from "./StatusBadge";

interface DocumentListProps {
	documents: RagDocument[];
	onDelete: (documentId: string) => void;
	onReprocess: (documentId: string) => void;
}

export function DocumentList({
	documents,
	onDelete,
	onReprocess,
}: DocumentListProps) {
	if (documents.length === 0) {
		return (
			<div className="text-center py-12">
				<FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
				<p className="text-muted-foreground">No documents uploaded yet</p>
			</div>
		);
	}

	const formatFileSize = (bytes: string) => {
		const size = Number(bytes);
		if (size < 1024) return `${size} B`;
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
		return `${(size / (1024 * 1024)).toFixed(2)} MB`;
	};

	return (
		<div className="rounded-md border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Filename</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Size</TableHead>
						<TableHead>Chunks</TableHead>
						<TableHead>Uploaded</TableHead>
						<TableHead className="text-right">Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{documents.map((doc) => (
						<TableRow key={doc.id}>
							<TableCell className="font-medium">{doc.filename}</TableCell>
							<TableCell>
								<StatusBadge
									status={doc.status}
									errorMessage={doc.errorMessage}
								/>
							</TableCell>
							<TableCell>{formatFileSize(doc.sizeBytes)}</TableCell>
							<TableCell>{doc.chunkCount}</TableCell>
							<TableCell className="text-muted-foreground">
								{formatDistanceToNow(new Date(doc.createdAt), {
									addSuffix: true,
								})}
							</TableCell>
							<TableCell className="text-right">
								<div className="flex items-center justify-end gap-2">
									{doc.status === "failed" && (
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onReprocess(doc.id)}
											title="Reprocess"
										>
											<RefreshCw className="h-4 w-4" />
										</Button>
									)}
									<AlertDialog>
										<AlertDialogTrigger asChild>
											<Button variant="ghost" size="icon" title="Delete">
												<Trash2 className="h-4 w-4" />
											</Button>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>Delete Document</AlertDialogTitle>
												<AlertDialogDescription>
													Are you sure you want to delete "{doc.filename}"? This
													will remove all chunks associated with this document
													and cannot be undone.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>Cancel</AlertDialogCancel>
												<AlertDialogAction onClick={() => onDelete(doc.id)}>
													Delete
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
